/**
 * Composer — text input with dual slash popups for the Edit-with-AI drawer.
 *
 * Popup header rows, popup item rows, and kbd hint row are copied verbatim
 * from designs/edit-with-ai-combined-6-9.html lines 407-472. Trigger
 * detection + keyboard nav are behavior our mocked version did not
 * implement (the design hard-codes `value.startsWith('@')`), so we keep
 * the robust handling and only align the JSX.
 *
 * Triggers:
 *   - `@` → persona popup (only pre-chat, and only if no persona is locked).
 *   - `/` → skill popup (always available).
 */

import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type { PersonaListItem } from '../../types/personas';
import type { SkillListItem } from '../../types/skills';
import type { ToolListItem } from '@shared/schemas/tools';
import { CapabilityPopover } from './CapabilityPopover';

/**
 * Returns the query text that follows a `@` or `/` trigger at the caret.
 * Returns `null` when there's no active trigger. Uses a capture-group regex
 * — NOT regex look-behind — for Safari compatibility.
 *
 * Exported (bead terra-design-v2-chat-yub.15) so `shell-next/chat/ChatComposerV2`
 * imports this exact pure function rather than forking a second copy —
 * coding-standards.md: "port the logic, never the markup." This is the ONE
 * name V2 is allowed to import from this module (see that bead's "Do NOT
 * Create" list) — the `Composer` component itself stays V1-only.
 */
export function detectTrigger(
  value: string,
  cursor: number,
  char: '@' | '/',
): string | null {
  const before = value.slice(0, cursor);
  const match = before.match(new RegExp(`(^|\\s)\\${char}([\\w-]*)$`));
  return match ? match[2] : null;
}

export interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSend: (message: string) => void;
  personas: PersonaListItem[];
  skills: SkillListItem[];
  /**
   * Tools the agent can call — surfaced via the capability rail below the
   * textarea. The list comes from GET /api/v4/tools (display metadata only;
   * the agent loop's execution registry is in code). Defaults to `[]` so
   * existing call sites that haven't been migrated still type-check.
   */
  tools?: ToolListItem[];
  /** The currently locked persona (null pre-chat). */
  persona: PersonaListItem | null;
  /** Derived: true once the chat has started. */
  started: boolean;
  onPickPersona: (p: PersonaListItem) => void;
  onPickSkill: (s: SkillListItem) => void;
  onOpenLibrary?: (tab: 'personas' | 'skills') => void;
  /** Open the read-only DebugInspector drawer (capability "?" popover footer). */
  onOpenInspector?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Additive-only action slot rendered in the input row, next to the
   * library button (flow-chat-multisession epic, U5 — the ONE new
   * component prop sanctioned across that epic; see
   * `plans/flow-chat-multisession/coding-standards.md` §1). Optional,
   * defaults to nothing rendered — every existing call site
   * (`KeystoneChatSurface`/`PreChatSurface` in `chat-panel-core.tsx`, and
   * V4's `KeystoneChatPanel.tsx`) is byte-compatible with no prop passed.
   */
  composerAccessory?: ReactNode;
}

type PopupKind = 'personas' | 'skills' | null;

export function Composer({
  value,
  onChange,
  onSend,
  personas,
  skills,
  tools = [],
  persona,
  started,
  onPickPersona,
  onPickSkill,
  onOpenLibrary,
  onOpenInspector,
  placeholder,
  disabled,
  composerAccessory,
}: ComposerProps) {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [cursor, setCursor] = useState<number>(value.length);
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const [toolsRailOpen, setToolsRailOpen] = useState(false);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (el.selectionStart !== cursor) {
      try {
        el.setSelectionRange(cursor, cursor);
      } catch {
        // Detached input — ignore.
      }
    }
  }, [cursor, value]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  const personaTrigger = detectTrigger(value, cursor, '@');
  const skillTrigger = detectTrigger(value, cursor, '/');

  const personaPopupActive =
    personaTrigger !== null && !started && persona === null;
  const skillPopupActive = skillTrigger !== null;

  const popup: PopupKind = personaPopupActive
    ? 'personas'
    : skillPopupActive
      ? 'skills'
      : null;

  const filteredPersonas = useMemo(() => {
    if (!personaPopupActive) return [];
    const q = (personaTrigger ?? '').toLowerCase();
    return personas
      .filter(
        (p) =>
          !q ||
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q),
      )
      .slice(0, 5);
  }, [personas, personaTrigger, personaPopupActive]);

  const filteredSkills = useMemo(() => {
    if (!skillPopupActive) return [];
    const q = (skillTrigger ?? '').toLowerCase();
    return skills
      .filter(
        (s) =>
          !q ||
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q),
      )
      .slice(0, 5);
  }, [skills, skillTrigger, skillPopupActive]);

  const activeList = popup === 'personas' ? filteredPersonas : filteredSkills;

  if (activeIdx > 0 && activeIdx >= activeList.length) {
    setActiveIdx(0);
  }

  const stripTriggerToken = useCallback(
    (char: '@' | '/'): { next: string; nextCursor: number } => {
      const before = value.slice(0, cursor);
      const after = value.slice(cursor);
      const match = before.match(new RegExp(`(^|\\s)\\${char}[\\w-]*$`));
      if (!match) return { next: value, nextCursor: cursor };
      const tokenStart = match.index! + match[1].length;
      const next = value.slice(0, tokenStart) + after;
      return { next, nextCursor: tokenStart };
    },
    [value, cursor],
  );

  const replaceSkillToken = useCallback(
    (skillId: string): { next: string; nextCursor: number } => {
      const before = value.slice(0, cursor);
      const after = value.slice(cursor);
      const match = before.match(/(^|\s)\/[\w-]*$/);
      if (!match) return { next: value, nextCursor: cursor };
      const tokenStart = match.index! + match[1].length;
      const insertion = `/${skillId} `;
      const next = value.slice(0, tokenStart) + insertion + after;
      return { next, nextCursor: tokenStart + insertion.length };
    },
    [value, cursor],
  );

  const pickPersona = useCallback(
    (p: PersonaListItem) => {
      onPickPersona(p);
      const { next, nextCursor } = stripTriggerToken('@');
      onChange(next);
      setCursor(nextCursor);
      setActiveIdx(0);
    },
    [onPickPersona, onChange, stripTriggerToken],
  );

  const pickSkill = useCallback(
    (s: SkillListItem) => {
      onPickSkill(s);
      // Use the canonical server-side slug (matches generateSlug). Fall back
      // to a name-derived slug for forward compatibility if the API ever
      // omits the field.
      const slug =
        s.slug ||
        s.name
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')
          .replace(/-+/g, '-')
          .replace(/^-+|-+$/g, '') ||
        'skill';
      const { next, nextCursor } = replaceSkillToken(slug);
      onChange(next);
      setCursor(nextCursor);
      setActiveIdx(0);
    },
    [onPickSkill, onChange, replaceSkillToken],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
      setCursor(e.target.selectionStart ?? e.target.value.length);
      setActiveIdx(0);
    },
    [onChange],
  );

  const handleSelect = useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const target = e.currentTarget;
      setCursor(target.selectionStart ?? 0);
    },
    [],
  );

  const activeIdxRef = useRef(activeIdx);
  activeIdxRef.current = activeIdx;
  const popupRef = useRef(popup);
  popupRef.current = popup;
  const filteredPersonasRef = useRef(filteredPersonas);
  filteredPersonasRef.current = filteredPersonas;
  const filteredSkillsRef = useRef(filteredSkills);
  filteredSkillsRef.current = filteredSkills;
  const valueRef = useRef(value);
  valueRef.current = value;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      const currentPopup = popupRef.current;
      const list =
        currentPopup === 'personas'
          ? filteredPersonasRef.current
          : currentPopup === 'skills'
            ? filteredSkillsRef.current
            : [];

      if (currentPopup) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIdx(Math.min(activeIdxRef.current + 1, Math.max(list.length - 1, 0)));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIdx(Math.max(activeIdxRef.current - 1, 0));
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey && list.length > 0) {
          e.preventDefault();
          const idx = activeIdxRef.current;
          if (currentPopup === 'personas') {
            pickPersona(filteredPersonasRef.current[idx]);
          } else {
            pickSkill(filteredSkillsRef.current[idx]);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          const v = valueRef.current;
          onChange(`${v} `);
          setCursor(v.length + 1);
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey && !currentPopup) {
        e.preventDefault();
        if (disabledRef.current) return;
        const trimmed = valueRef.current.trim();
        if (!trimmed) return;
        onSend(trimmed);
      }
    },
    [pickPersona, pickSkill, onChange, onSend],
  );

  const effectivePlaceholder =
    placeholder ??
    (!started && persona === null
      // Persona selection is OPTIONAL, not a gate — sending a plain question
      // works immediately (Spencer QA: the old copy read as "you must @ a
      // persona before you can type," which isn't true).
      ? 'Ask a question, or type @ for a persona…'
      : 'Reply — or / to pick a skill…');

  return (
    <div className="p-3 border-t border-nightshade-200 dark:border-nightshade-700 relative">
      {/* Tools capability rail popover — design lines 254-277 of
          designs/tool-discovery-options.html. Each tool row is a native
          <details> for click-to-expand without per-row state. The rail
          deliberately sits below the slash/persona popups (z-10 vs z-20)
          so an autocomplete that pops up while the rail is open layers
          on top instead of being obscured. */}
      {toolsRailOpen && (
        <div className="absolute bottom-full left-3 right-3 mb-2 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.12)] z-10 max-h-80 overflow-y-auto">
          <div className="px-3 py-2 border-b border-nightshade-100 dark:border-nightshade-800 sticky top-0 bg-white dark:bg-nightshade-900 z-10">
            <div className="text-[10px] uppercase tracking-wider text-tangerine-700 dark:text-tangerine-400 font-semibold">
              ⚙ Tools the model can call
            </div>
            <div className="text-[10px] text-nightshade-400 dark:text-nightshade-500 mt-0.5">
              Available passively — the model picks when to use them. Click any
              tool to read its full description.
            </div>
          </div>
          {tools.map((t) => (
            <details
              key={t.slug}
              className="border-b border-nightshade-100 dark:border-nightshade-800 group last:border-0"
            >
              <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-nightshade-50 dark:hover:bg-nightshade-800 list-none">
                <span className="w-6 h-6 flex items-center justify-center bg-nightshade-100 dark:bg-nightshade-800 rounded text-sm">
                  {t.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-nightshade-900 dark:text-nightshade-100">
                    {t.label}
                  </div>
                  <div className="text-[10px] text-nightshade-500 dark:text-nightshade-400 truncate">
                    {t.short}
                  </div>
                </div>
                <span className="text-nightshade-400 dark:text-nightshade-500 group-open:rotate-90 transition-transform text-xs">
                  ›
                </span>
              </summary>
              <div className="px-3 pb-2.5 pt-1 bg-nightshade-50 dark:bg-nightshade-800 text-[11px] text-nightshade-700 dark:text-nightshade-300 leading-relaxed">
                <div className="text-[10px] text-nightshade-400 dark:text-nightshade-500 font-mono mb-1">
                  {t.slug}
                </div>
                {t.long}
              </div>
            </details>
          ))}
        </div>
      )}

      {/* Persona popup — design lines 410-431. z-20 so it lays over the
          tools rail (z-10) when both happen to be open. */}
      {popup === 'personas' && filteredPersonas.length > 0 && (
        <div
          role="listbox"
          aria-label="Personas"
          className="absolute bottom-full left-3 right-3 mb-2 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-lg shadow-lg overflow-hidden z-20"
        >
          <div className="text-[10px] text-ube-600 dark:text-ube-400 uppercase tracking-wider px-3 py-1.5 bg-ube-50 dark:bg-ube-900/30 font-semibold flex items-center justify-between">
            <span>🪄 Personas · pick one to start chat</span>
            <span className="text-nightshade-500 dark:text-nightshade-400 normal-case font-normal">↑↓ ↵ esc</span>
          </div>
          {filteredPersonas.map((p, i) => (
            <div
              key={p.id}
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => {
                e.preventDefault();
                pickPersona(p);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                i === activeIdx ? 'bg-ube-50 dark:bg-ube-900/30' : 'hover:bg-nightshade-50 dark:hover:bg-nightshade-800'
              }`}
            >
              <span className="w-6 h-6 flex items-center justify-center bg-nightshade-100 dark:bg-nightshade-800 rounded text-sm">
                {p.icon ?? '🪄'}
              </span>
              <div className="flex-1">
                <div className="text-sm font-medium text-nightshade-900 dark:text-nightshade-100">{p.name}</div>
                {p.description && (
                  <div className="text-[11px] text-nightshade-500 dark:text-nightshade-400">{p.description}</div>
                )}
              </div>
              {p.visibility === 'team' && (
                <span className="text-[9px] px-1 py-0.5 bg-matcha-100 text-matcha-700 dark:bg-matcha-900/40 dark:text-matcha-300 rounded">
                  TEAM
                </span>
              )}
              {p.isPinned && <span className="text-[10px] text-lemon-400 dark:text-lemon-300">★</span>}
            </div>
          ))}
        </div>
      )}

      {/* Skills popup — design lines 433-452. z-20 so it lays over the
          tools rail (z-10) when both happen to be open. */}
      {popup === 'skills' && filteredSkills.length > 0 && (
        <div
          role="listbox"
          aria-label="Skills"
          className="absolute bottom-full left-3 right-3 mb-2 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-lg shadow-lg overflow-hidden z-20"
        >
          <div className="text-[10px] text-blueberry-600 dark:text-blueberry-400 uppercase tracking-wider px-3 py-1.5 bg-blueberry-50 dark:bg-blueberry-900/30 font-semibold flex items-center justify-between">
            <span>⚡ Skills</span>
            <span className="text-nightshade-500 dark:text-nightshade-400 normal-case font-normal">↑↓ ↵ esc</span>
          </div>
          {filteredSkills.map((s, i) => (
            <div
              key={s.id}
              role="option"
              aria-selected={i === activeIdx}
              onMouseDown={(e) => {
                e.preventDefault();
                pickSkill(s);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                i === activeIdx ? 'bg-blueberry-50 dark:bg-blueberry-900/30' : 'hover:bg-nightshade-50 dark:hover:bg-nightshade-800'
              }`}
            >
              <span className="w-6 h-6 flex items-center justify-center bg-nightshade-100 dark:bg-nightshade-800 rounded text-sm">
                {s.icon ?? '⚡'}
              </span>
              <div className="flex-1">
                <div className="text-sm font-medium text-nightshade-900 dark:text-nightshade-100">{s.name}</div>
                {s.description && (
                  <div className="text-[11px] text-nightshade-500 dark:text-nightshade-400">{s.description}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Textarea + ☰ library button (design line 453-463; ☰ taken from the
          mobile variant around line 608).

          Round-7 QA fix (2026-08-20): `items-end` bottom-aligned every child
          against the textarea, but `CapabilityPopover`'s "?" button is a
          fixed `h-9` square and `composerAccessory` (the Stop button, when
          rendered — see `FlowChatPanel.tsx`) was text-padding-sized with no
          fixed height, so both read shorter/misaligned next to a
          single-line textarea. `items-center` handles the common
          single-line case correctly and still reads fine once the textarea
          grows (`max-h-32 overflow-y-auto`) — centering fixed-size side
          buttons next to a taller box is a normal pattern, not a special
          case needing its own branch. */}
      <div className="flex items-center gap-2">
        <textarea
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onSelect={handleSelect}
          onKeyUp={handleSelect}
          onClick={handleSelect}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={effectivePlaceholder}
          aria-label="Message input"
          className="flex-1 resize-none text-sm px-3 py-2 bg-white dark:bg-nightshade-900 text-content-primary dark:text-nightshade-100 placeholder:text-content-placeholder border border-nightshade-200/50 dark:border-nightshade-700/50 rounded-xl shadow-md hover:shadow-lg focus:outline-none focus:shadow-xl focus:border-blueberry-300 dark:focus:border-blueberry-700 focus:ring-2 focus:ring-blueberry-100 dark:focus:ring-blueberry-900/40 disabled:opacity-50 transition-shadow max-h-32 overflow-y-auto"
        />
        <CapabilityPopover onOpenInspector={onOpenInspector ?? (() => {})} />
        {composerAccessory}
        {onOpenLibrary && (
          <button
            type="button"
            onClick={() =>
              onOpenLibrary(started || persona !== null ? 'skills' : 'personas')
            }
            className="text-xs px-2.5 py-2 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-md text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 flex-shrink-0"
            title="Open toolkit"
            aria-label="Open toolkit"
          >
            ☰
          </button>
        )}
      </div>

      {/* Hint row — capability rail pill on the left, trigger hints on the
          right. Pill JSX copied verbatim from designs/tool-discovery-options.html
          lines 285-298. */}
      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          onClick={() => setToolsRailOpen((v) => !v)}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium border transition-colors bg-nightshade-100 border-nightshade-200 text-nightshade-600 hover:bg-nightshade-200 dark:bg-nightshade-800 dark:border-nightshade-700 dark:text-nightshade-400 dark:hover:bg-nightshade-700"
          aria-expanded={toolsRailOpen}
          aria-label={
            toolsRailOpen ? 'Hide tools list' : 'Show tools the model can call'
          }
        >
          <span>⚙</span>
          <span>{tools.length} tools</span>
          {!toolsRailOpen && (
            <>
              <span className="opacity-60">·</span>
              <span className="opacity-80">click to inspect</span>
            </>
          )}
        </button>
        <span className="text-[10px] text-nightshade-400 dark:text-nightshade-500 flex items-center gap-1">
          {!started && persona === null && (
            <>
              <span className="px-1.5 py-0.5 rounded bg-ube-100 border border-ube-200 text-ube-700 dark:bg-ube-900/40 dark:border-ube-800 dark:text-ube-400 font-mono font-bold text-[11px]">@</span>
              <span>personas</span>
              <span className="text-nightshade-300 dark:text-nightshade-600 mx-0.5">·</span>
            </>
          )}
          <span className="px-1.5 py-0.5 rounded bg-blueberry-100 border border-blueberry-200 text-blueberry-700 dark:bg-blueberry-900/40 dark:border-blueberry-800 dark:text-blueberry-400 font-mono font-bold text-[11px]">/</span>
          <span>skills</span>
          {onOpenLibrary && (
            <>
              <span className="text-nightshade-300 dark:text-nightshade-600 mx-0.5">·</span>
              <span>or click in sidebar →</span>
            </>
          )}
        </span>
      </div>
    </div>
  );
}

